import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  RestaurantMenu as RecipeIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import recipeAPI, { RecipeIngredient } from "../../services/recipeAPI";
import { productAPI, UNIT_OPTIONS } from "../../services/productAPI";
import { useRBAC } from "../../hooks/useRBAC";
import { useTradeTerminology } from "../../utils/tradeTerminology";
import PageHeader from "../../components/layout/PageHeader";
import DataListCard from "../../components/layout/DataListCard";
import ConfirmDialog from "../../components/layout/ConfirmDialog";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";

const RecipesPage: React.FC = () => {
  const { confirm, dialogProps: confirmDialogProps } = useConfirmDialog();
  const { terms } = useTradeTerminology();
  const { canCreate, canDelete, canUpdate } = useRBAC();
  const canCreateRecipe = canCreate("recipes") || canCreate("products");
  const canUpdateRecipe = canUpdate("recipes") || canUpdate("products");
  const canDeleteRecipe = canDelete("recipes") || canDelete("products");
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const productIdFilter = searchParams.get("productId") || "";
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [alert, setAlert] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    productId: productIdFilter,
    name: "",
    yield: 1,
    yieldUnit: "PCS",
    ingredientProductId: "",
    ingredientQty: 1,
    ingredientUnit: "PCS",
  });
  const [ingredients, setIngredients] = useState<RecipeIngredient[]>([]);

  useEffect(() => {
    if (productIdFilter) {
      setForm((f) => ({ ...f, productId: productIdFilter }));
      setPage(0);
    }
  }, [productIdFilter]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["recipes", page, rowsPerPage, search, productIdFilter],
    queryFn: () =>
      recipeAPI.getRecipes({
        pageNo: page + 1,
        pageSize: rowsPerPage,
        search: search || undefined,
        productId: productIdFilter || undefined,
      }),
  });

  const { data: preparedProducts } = useQuery({
    queryKey: ["prepared-products"],
    queryFn: async () => {
      const res = await productAPI.getProducts({
        pageNo: 1,
        pageSize: 200,
        productType: "PREPARED",
      } as any);
      return res.data || [];
    },
    enabled: open,
  });

  const { data: goodsProducts } = useQuery({
    queryKey: ["goods-products"],
    queryFn: async () => {
      const res = await productAPI.getProducts({
        pageNo: 1,
        pageSize: 200,
        productType: "GOODS",
      } as any);
      return res.data || [];
    },
    enabled: open,
  });

  const createMutation = useMutation({
    mutationFn: recipeAPI.createRecipe,
    onSuccess: () => {
      setAlert({ type: "success", message: "Recipe created" });
      setOpen(false);
      setIngredients([]);
      queryClient.invalidateQueries({ queryKey: ["recipes"] });
    },
    onError: (e: any) =>
      setAlert({
        type: "error",
        message: e?.response?.data?.message || e?.message || "Create failed",
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: recipeAPI.deleteRecipe,
    onSuccess: () => {
      setAlert({ type: "success", message: "Recipe deleted" });
      queryClient.invalidateQueries({ queryKey: ["recipes"] });
    },
    onError: (e: any) =>
      setAlert({
        type: "error",
        message: e?.response?.data?.message || e?.message || "Delete failed",
      }),
  });

  const toggleMutation = useMutation({
    mutationFn: recipeAPI.toggleActive,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["recipes"] }),
  });

  const rows = data?.data || [];
  const total = data?.total || 0;

  const preparedOptions = useMemo(() => preparedProducts || [], [preparedProducts]);
  const goodsOptions = useMemo(() => goodsProducts || [], [goodsProducts]);

  function addIngredient() {
    if (!form.ingredientProductId || !(form.ingredientQty > 0)) return;
    const product = goodsOptions.find((p: any) => p.id === form.ingredientProductId);
    setIngredients((prev) => [
      ...prev,
      {
        ingredientProductId: form.ingredientProductId,
        quantity: Number(form.ingredientQty),
        unit: form.ingredientUnit || product?.unit || "PCS",
        wastagePercent: 0,
        ingredientProduct: product
          ? { id: product.id, name: product.name, unit: product.unit || "PCS" }
          : undefined,
      },
    ]);
    setForm((f) => ({ ...f, ingredientProductId: "", ingredientQty: 1 }));
  }

  function submitCreate() {
    if (!form.productId || !form.name.trim() || !ingredients.length) {
      setAlert({
        type: "error",
        message: "Prepared product, name, and at least one ingredient required",
      });
      return;
    }
    createMutation.mutate({
      productId: form.productId,
      name: form.name.trim(),
      yield: Number(form.yield) || 1,
      yieldUnit: form.yieldUnit,
      isActive: true,
      ingredients: ingredients.map((i) => ({
        ingredientProductId: i.ingredientProductId,
        quantity: i.quantity,
        unit: i.unit,
        wastagePercent: i.wastagePercent || 0,
      })),
    });
  }

  return (
    <Box>
      <PageHeader
        title={terms.recipesLabel}
        subtitle={
          productIdFilter
            ? "Filtered by prepared product from Product Master"
            : undefined
        }
        breadcrumbs={[{ label: "Dashboard", to: "/dashboard" }, { label: terms.recipesLabel }]}
        action={
          <Stack direction="row" spacing={1}>
            <IconButton onClick={() => refetch()}>
              <RefreshIcon />
            </IconButton>
            {canCreateRecipe && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setOpen(true);
                  setIngredients([]);
                  setForm({
                    productId: productIdFilter,
                    name: "",
                    yield: 1,
                    yieldUnit: "PCS",
                    ingredientProductId: "",
                    ingredientQty: 1,
                    ingredientUnit: "PCS",
                  });
                }}
              >
                {terms.recipesAddButton}
              </Button>
            )}
          </Stack>
        }
      />

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      <DataListCard
        searchPlaceholder="Search recipes…"
        searchValue={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(0);
        }}
        footer={
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{ width: "100%" }}
          />
        }
      >
        <TableContainer>
          <Table size="small" sx={{ width: "100%" }}>
            <TableHead>
              <TableRow>
                <TableCell>Recipe</TableCell>
                <TableCell>Prepared product</TableCell>
                <TableCell>Yield</TableCell>
                <TableCell>Ingredients</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Stack alignItems="center" py={5} spacing={1}>
                      <RecipeIcon color="disabled" fontSize="large" />
                      <Typography color="text.secondary">No recipes yet</Typography>
                      {canCreate("recipes") && (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            setOpen(true);
                            setIngredients([]);
                            setForm({
                              productId: productIdFilter,
                              name: "",
                              yield: 1,
                              yieldUnit: "PCS",
                              ingredientProductId: "",
                              ingredientQty: 1,
                              ingredientUnit: "PCS",
                            });
                          }}
                        >
                          New recipe
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {r.name}
                    </Typography>
                  </TableCell>
                  <TableCell>{r.product?.name || r.productId}</TableCell>
                  <TableCell>
                    {Number(r.yield)} {r.yieldUnit}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={`${r.ingredients?.length || 0} items`}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={r.isActive ? "Active" : "Inactive"}
                      color={r.isActive ? "success" : "default"}
                      onClick={() =>
                        canUpdateRecipe && toggleMutation.mutate(r.id)
                      }
                      sx={{
                        cursor: canUpdateRecipe ? "pointer" : "default",
                        ...(r.isActive
                          ? { color: "#fff", "& .MuiChip-label": { color: "#fff" } }
                          : {}),
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {canDeleteRecipe && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={async () => {
                          const ok = await confirm({
                            title: "Delete recipe",
                            message: `Delete recipe “${r.name}”? This cannot be undone.`,
                            confirmLabel: "Delete",
                            tone: "danger",
                          });
                          if (ok) deleteMutation.mutate(r.id);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DataListCard>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={700}>
              New recipe
            </Typography>
            <IconButton size="small" onClick={() => setOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: "72vh" }}>
          <Stack spacing={2} mt={1}>
            <TextField
              select
              label="Prepared product"
              value={form.productId}
              onChange={(e) => {
                const id = e.target.value;
                const p = preparedOptions.find((x: any) => x.id === id);
                setForm((f) => ({
                  ...f,
                  productId: id,
                  name: f.name || (p ? `${p.name} recipe` : ""),
                  yieldUnit: p?.unit || f.yieldUnit,
                }));
              }}
              fullWidth
            >
              {preparedOptions.map((p: any) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Recipe name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Yield"
                type="number"
                value={form.yield}
                onChange={(e) =>
                  setForm((f) => ({ ...f, yield: Number(e.target.value) || 1 }))
                }
                fullWidth
              />
              <TextField
                select
                label="Yield unit"
                value={form.yieldUnit}
                onChange={(e) => setForm((f) => ({ ...f, yieldUnit: e.target.value }))}
                fullWidth
              >
                {UNIT_OPTIONS.map((u) => (
                  <MenuItem key={u.value} value={u.value}>
                    {u.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Typography variant="subtitle2" fontWeight={700}>
              Ingredients (GOODS)
            </Typography>
            <Stack spacing={1.5}>
              <TextField
                select
                label="Ingredient"
                value={form.ingredientProductId}
                onChange={(e) => {
                  const id = e.target.value;
                  const item = goodsOptions.find((p: any) => p.id === id);
                  setForm((f) => ({
                    ...f,
                    ingredientProductId: id,
                    ingredientUnit: item?.unit || f.ingredientUnit || "PCS",
                  }));
                }}
                fullWidth
                size="small"
              >
                {goodsOptions.map((p: any) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  label="Qty"
                  type="number"
                  size="small"
                  inputProps={{ step: "any", min: 0.0001 }}
                  value={form.ingredientQty}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      ingredientQty: Number(e.target.value) || 0,
                    }))
                  }
                  sx={{ width: 120 }}
                />
                <TextField
                  select
                  label="Unit"
                  size="small"
                  value={form.ingredientUnit}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, ingredientUnit: e.target.value }))
                  }
                  sx={{ width: 140 }}
                >
                  {UNIT_OPTIONS.map((u) => (
                    <MenuItem key={u.value} value={u.value}>
                      {u.label}
                    </MenuItem>
                  ))}
                </TextField>
                <Button variant="outlined" onClick={addIngredient} sx={{ mt: 0.25 }}>
                  Add
                </Button>
              </Stack>
            </Stack>
            {ingredients.length > 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell>Unit</TableCell>
                    <TableCell width={48} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ingredients.map((ing, idx) => (
                    <TableRow key={`${ing.ingredientProductId}-${idx}`}>
                      <TableCell>
                        {ing.ingredientProduct?.name || ing.ingredientProductId}
                      </TableCell>
                      <TableCell align="right">{ing.quantity}</TableCell>
                      <TableCell>{ing.unit}</TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() =>
                            setIngredients((prev) => prev.filter((_, i) => i !== idx))
                          }
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitCreate}
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? "Creating…" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog {...confirmDialogProps} />
    </Box>
  );
};

export default RecipesPage;
